import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, ShieldCheck, FileText, ShoppingBag, PhoneCall, FileSpreadsheet, CalendarClock } from "lucide-react";

const TYPE_META = {
  insurance: { icon: ShieldCheck, label: "Existing policy", dot: "bg-emerald-600" },
  vault: { icon: FileText, label: "Policy vault", dot: "bg-emerald-800" },
  issued: { icon: ShieldCheck, label: "Policy issued", dot: "bg-amber-500" },
  crece: { icon: ShoppingBag, label: "Crece product", dot: "bg-sky-600" },
  lead: { icon: PhoneCall, label: "Lead", dot: "bg-stone-400" },
  quote: { icon: FileSpreadsheet, label: "Offline quote", dot: "bg-indigo-500" },
};

const STATUS_STYLE = (s) =>
  ["paid", "approved", "active", "policy issued", "policy_issued", "converted"].includes(s) ? "bg-emerald-100 text-emerald-800"
    : ["rejected", "dead", "cancelled"].includes(s) ? "bg-red-100 text-red-700"
    : "bg-stone-100 text-stone-600";

const renewalTone = (d) => (d <= 30 ? "bg-red-100 text-red-700 border-red-200" : d <= 90 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200");

export const PolicyTimeline = ({ clientId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/clients/${clientId}/timeline`).then((r) => setData(r.data)).catch(() => setData({ events: [], renewals: [], counts: {} }));
  }, [clientId]);

  if (!data) return <div className="py-12 text-center text-stone-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading timeline…</div>;

  return (
    <div className="space-y-6" data-testid="policy-timeline">
      {/* summary chips */}
      <div className="flex flex-wrap gap-3">
        <div className="bg-emerald-900 text-white rounded-2xl px-5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-100/60">Policies</div>
          <div className="font-display text-2xl font-black" data-testid="timeline-policies-count">{data.counts.policies || 0}</div>
        </div>
        <div className="bg-stone-100 rounded-2xl px-5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Crece purchases</div>
          <div className="font-display text-2xl font-black">{data.counts.purchases || 0}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Renewals ≤ 90 days</div>
          <div className="font-display text-2xl font-black">{data.counts.renewals_due || 0}</div>
        </div>
      </div>

      {/* upcoming renewals */}
      {data.renewals.length > 0 && (
        <div data-testid="timeline-renewals">
          <div className="text-xs font-black uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Upcoming renewals</div>
          <div className="flex flex-wrap gap-2">
            {data.renewals.map((r, i) => (
              <span key={i} className={`inline-flex items-center gap-2 text-xs font-bold border rounded-full px-3.5 py-1.5 ${renewalTone(r.days_left)}`}>
                {r.title}
                <span className="font-black">{r.days_left === 0 ? "today" : `${r.days_left}d`}</span>
                <span className="opacity-60 font-normal">{r.date}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* timeline */}
      {data.events.length === 0 ? (
        <div className="text-center py-14 text-stone-400" data-testid="timeline-empty">
          No policies or activity for this client yet — issued policies, quotes and leads will appear here automatically.
        </div>
      ) : (
        <div className="relative pl-6" data-testid="timeline-events">
          <div className="absolute left-[9px] top-1 bottom-1 w-px bg-stone-200" />
          <div className="space-y-4">
            {data.events.map((e, i) => {
              const meta = TYPE_META[e.type] || TYPE_META.lead;
              const Icon = meta.icon;
              return (
                <div key={i} className="relative" data-testid={`timeline-event-${i}`}>
                  <span className={`absolute -left-6 top-1.5 w-[19px] h-[19px] rounded-full ${meta.dot} grid place-items-center ring-4 ring-white`}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </span>
                  <div className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-stone-400">{meta.label}</span>
                    <span className="text-[11px] text-stone-400">{e.date}</span>
                    <div className="w-full sm:w-auto sm:flex-1">
                      <span className="font-bold text-sm">{e.title}</span>
                      {e.subtitle && <span className="text-xs text-stone-500 ml-2">{e.subtitle}</span>}
                    </div>
                    {e.status && <span className={`text-[10px] font-black uppercase rounded-full px-2.5 py-1 ${STATUS_STYLE(e.status)}`}>{e.status}</span>}
                    {e.renewal_date && e.days_left !== null && e.days_left >= 0 && (
                      <span className={`text-[10px] font-bold border rounded-full px-2.5 py-1 ${renewalTone(e.days_left)}`}>renews {e.renewal_date} · {e.days_left}d</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyTimeline;
